import React, { useRef, useEffect, useState } from 'react'
import { ZinButton } from '@tnl/zin-react'
import { IOwner } from '@features/account/types'
import { formatPhoneNumberWithCountry } from '@utils/formattingUtils'
import {
  PROFILE_PERSONAL_INFORMATION_TITLE,
  PROFILE_EMAIL_ADDRESS_SECTION,
  PROFILE_EMAIL_LABEL,
  PROFILE_ADDRESS_SECTION,
  PROFILE_COUNTRY_LABEL,
  PROFILE_ADDRESS_LINE_1_LABEL,
  PROFILE_ADDRESS_LINE_2_LABEL,
  PROFILE_ADDRESS_LINE_3_LABEL,
  PROFILE_CITY_LABEL,
  PROFILE_STATE_LABEL,
  PROFILE_POSTAL_CODE_LABEL,
  PROFILE_PHONE_NUMBER_SECTION,
  PROFILE_HOME_PHONE_LABEL,
  PROFILE_MOBILE_PHONE_LABEL,
  PROFILE_WORK_PHONE_LABEL,
  PROFILE_EDIT_BUTTON_TEXT,
} from '@constants'
import EditInfoForm from '@account/components/EditInfoForm/EditInfoForm'
import './ProfileInfoBox.scss'

interface ProfileInfoBoxProps {
  owner: IOwner
}

const ProfileInfoBox: React.FC<ProfileInfoBoxProps> = ({ owner }) => {
  const [isEditingInfo, setIsEditingInfo] = useState(false)
  const [isInfoContentVisible, setIsInfoContentVisible] = useState(false)
  const infoContentRef = useRef<HTMLDivElement>(null)

  // Intersection observer to detect when info content is visible
  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => {
        setIsInfoContentVisible(entry.isIntersecting)
      },
      {
        threshold: 0.1, // Trigger when 10% of the element is visible
        rootMargin: '-100px 0px 0px 0px', // Start detecting 100px before the element comes into view
      },
    )

    if (infoContentRef.current) {
      observer.observe(infoContentRef.current)
    }

    return () => observer.disconnect()
  }, [])

  const handleEditInfo = () => {
    setIsEditingInfo(true)
  }

  // EditInfoForm runs `refreshProfile` itself after a save (to verify what persisted), so
  // closing the dialog here does not need to trigger another GET. Cancel also doesn't change
  // anything server-side, so no refresh is needed on cancel either.
  const handleCloseEditDialog = () => {
    setIsEditingInfo(false)
  }

  return (
    <div className="mk-profile__info-box">
      <h3 className="mk-profile__info-title title-4">{PROFILE_PERSONAL_INFORMATION_TITLE}</h3>

      <div
        className="mk-profile__info-content"
        ref={infoContentRef}>
        {!isEditingInfo ? (
          <>
            <div className="mk-profile__info-section">
              <div className="mk-profile__info-section-label body-2 text-accent font-bold">{PROFILE_EMAIL_ADDRESS_SECTION}</div>
              <div className="mk-profile__info-section-row">
                <div className="mk-profile__info-label body-1">{PROFILE_EMAIL_LABEL}</div>
                <div className="mk-profile__info-value body-1">{owner.emailAddress || 'Not provided'}</div>
              </div>
            </div>
            <div className="mk-profile__section-divider" />
            <div className="mk-profile__info-section">
              <div className="mk-profile__info-section-label body-2 text-accent font-bold">{PROFILE_ADDRESS_SECTION}</div>
              <div className="mk-profile__info-section-row">
                <div className="mk-profile__info-label body-1">{PROFILE_COUNTRY_LABEL}</div>
                <div className="mk-profile__info-value body-1">{owner.country || 'Not provided'}</div>
              </div>
              <div className="mk-profile__info-section-row">
                <div className="mk-profile__info-label body-1">{PROFILE_ADDRESS_LINE_1_LABEL}</div>
                <div className="mk-profile__info-value body-1">{owner.addressLine1 || 'Not provided'}</div>
              </div>
              {owner.addressLine2 && (
                <div className="mk-profile__info-section-row">
                  <div className="mk-profile__info-label body-1">{PROFILE_ADDRESS_LINE_2_LABEL}</div>
                  <div className="mk-profile__info-value body-1">{owner.addressLine2}</div>
                </div>
              )}
              {owner.addressLine3 && (
                <div className="mk-profile__info-section-row">
                  <div className="mk-profile__info-label body-1">{PROFILE_ADDRESS_LINE_3_LABEL}</div>
                  <div className="mk-profile__info-value body-1">{owner.addressLine3}</div>
                </div>
              )}
              {owner.city && (
                <div className="mk-profile__info-section-row">
                  <div className="mk-profile__info-label body-1">{PROFILE_CITY_LABEL}</div>
                  <div className="mk-profile__info-value body-1">{owner.city}</div>
                </div>
              )}
              {owner.stateProvince && (
                <div className="mk-profile__info-section-row">
                  <div className="mk-profile__info-label body-1">{PROFILE_STATE_LABEL}</div>
                  <div className="mk-profile__info-value body-1">{owner.stateProvince}</div>
                </div>
              )}
              {owner.postalCode && (
                <div className="mk-profile__info-section-row">
                  <div className="mk-profile__info-label body-1">{PROFILE_POSTAL_CODE_LABEL}</div>
                  <div className="mk-profile__info-value body-1">{owner.postalCode}</div>
                </div>
              )}
            </div>
            <div className="mk-profile__section-divider" />
            <div className="mk-profile__info-section">
              <div className="mk-profile__info-section-label body-2 text-accent font-bold">{PROFILE_PHONE_NUMBER_SECTION}</div>
              {owner.phoneHome?.phoneNumber && (
                <div className="mk-profile__info-section-row">
                  <div className="mk-profile__info-label body-1">{PROFILE_HOME_PHONE_LABEL}</div>
                  <div className="mk-profile__info-value body-1">{formatPhoneNumberWithCountry(owner.phoneHome.phoneNumber, owner.phoneHome.countryCode)}</div>
                </div>
              )}
              {owner.phoneMobile?.phoneNumber && (
                <div className="mk-profile__info-section-row">
                  <div className="mk-profile__info-label body-1">{PROFILE_MOBILE_PHONE_LABEL}</div>
                  <div className="mk-profile__info-value body-1">{formatPhoneNumberWithCountry(owner.phoneMobile.phoneNumber, owner.phoneMobile.countryCode)}</div>
                </div>
              )}
              {owner.phoneWork?.phoneNumber && (
                <div className="mk-profile__info-section-row">
                  <div className="mk-profile__info-label body-1">{PROFILE_WORK_PHONE_LABEL}</div>
                  <div className="mk-profile__info-value body-1">{formatPhoneNumberWithCountry(owner.phoneWork.phoneNumber, owner.phoneWork.countryCode)}</div>
                </div>
              )}
            </div>
            <div className={`mk-profile__cta ${isInfoContentVisible ? 'mk-profile__cta--sticky' : ''}`}>
              <ZinButton
                variant="primary"
                full
                onClick={handleEditInfo}>
                {PROFILE_EDIT_BUTTON_TEXT}
              </ZinButton>
            </div>
          </>
        ) : (
          <EditInfoForm
            owner={owner}
            onCancel={handleCloseEditDialog}
            onSave={handleCloseEditDialog}
          />
        )}
      </div>
    </div>
  )
}

export default ProfileInfoBox
